import { useCallback, useEffect, useState } from "react";
import { History, Loader2, Square, CheckCircle2, AlertTriangle } from "lucide-react";

import { Button } from "../ui/button";
import { cn } from "../../lib/utils";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";

// O painel de importação, no alto da tela de Conexões.
//
// Trazer anos de conversa leva horas e acontece longe da vista: o servidor
// varre sozinho e a pessoa não tem como saber se ainda está indo. Sem isto o
// único sinal era abrir o modal de uma conexão específica e olhar — e quem
// acabou de parear nem sabe que existe modal.
//
// Fica fora do modal de propósito. É um estado da empresa, não de um botão:
// enquanto estiver rodando, aparece em cima de tudo, em qualquer aba de
// Conexões, e some sozinho quando termina.

const SITUACOES = {
  pending: { rotulo: "Aguardando começar", andando: true },
  collecting: { rotulo: "Preparando a conexão", andando: true },
  processing: { rotulo: "Trazendo as conversas", andando: true },
  completed: { rotulo: "Importação concluída", andando: false },
  partial: { rotulo: "Importação parcial", andando: false },
  cancelled: { rotulo: "Importação cancelada", andando: false },
  failed: { rotulo: "A importação falhou", andando: false },
  coverage_unknown: { rotulo: "Alcance desconhecido", andando: false },
};

const emData = (v) => (v ? new Date(v).toLocaleDateString("pt-BR") : null);

const ImportacaoEmAndamento = ({ whatsApps }) => {
  const [lotes, setLotes] = useState([]);
  // Progresso vivo, por conexão. Vem do socket e é o que muda de segundo em
  // segundo; o lote no banco só é reescrito a cada rodada.
  const [progresso, setProgresso] = useState({});
  const [parando, setParando] = useState(null);
  // Rodada encerrada que a pessoa já leu. Sem isto o aviso de "concluída"
  // ficaria para sempre na tela.
  const [dispensados, setDispensados] = useState([]);

  // Só conexões que pediram histórico têm rodada. Consultar as outras seria
  // uma requisição por conexão a cada carregamento da tela, à toa.
  const candidatas = (whatsApps || []).filter(
    (w) => w.channel === "whatsapp" && (w.importHistory || w.restoringHistory),
  );

  const ids = candidatas.map((w) => w.id).join(",");

  const carregar = useCallback(async () => {
    if (!ids) {
      setLotes([]);
      return;
    }

    try {
      const respostas = await Promise.all(
        ids
          .split(",")
          .map((id) =>
            api
              .get(`/whatsapp/${id}/import-jobs`)
              .then(({ data }) => (Array.isArray(data) ? data : []))
              .catch(() => []),
          ),
      );

      // Só a rodada mais recente de cada conexão, e nunca um ensaio: ensaio não
      // grava nada e não tem andamento para acompanhar.
      setLotes(
        respostas
          .map((lista) => lista.filter((l) => !l.dryRun)[0])
          .filter(Boolean),
      );
    } catch (err) {
      toastError(err);
    }
  }, [ids]);

  useEffect(() => {
    carregar();

    const socket = openSocket();

    // Duas fontes, porque elas contam coisas diferentes: `import` é o pulso da
    // varredura (quantas vieram até agora), `importJob` é a mudança de
    // situação da rodada.
    socket.on("import", (d) => {
      if (!d?.whatsappId) return;

      setProgresso((antes) => ({
        ...antes,
        [d.whatsappId]: { ...(antes[d.whatsappId] || {}), ...d },
      }));

      if (d.action === "finished") carregar();
    });

    socket.on("importJob", () => carregar());

    return () => socket.disconnect();
  }, [carregar]);

  const parar = async (lote) => {
    setParando(lote.id);

    try {
      await api.post(`/import-jobs/${lote.id}/cancel`);
      await carregar();
    } catch (err) {
      toastError(err);
    }

    setParando(null);
  };

  const visiveis = lotes.filter((l) => !dispensados.includes(l.id));

  if (!visiveis.length) return null;

  return (
    <div className="mb-4 space-y-3">
      {visiveis.map((lote) => {
        const situacao = SITUACOES[lote.status] || {
          rotulo: lote.status,
          andando: false,
        };

        const conexao = candidatas.find((w) => w.id === lote.whatsappId);
        const vivo = progresso[lote.whatsappId] || {};

        // O número que a pessoa quer ver é "quantas já vieram". O do socket é
        // mais novo que o do banco enquanto a rodada anda.
        const mensagens = Math.max(
          Number(vivo.total || vivo.mensagens || 0),
          Number(lote.imported || 0),
        );

        const recuouAte = emData(vivo.maisAntiga || lote.oldestReceivedAt);

        return (
          <div
            key={lote.id}
            className={cn(
              "rounded-lg border p-4",
              situacao.andando
                ? "border-primary/40 bg-primary/5"
                : lote.status === "failed"
                  ? "border-destructive/40 bg-destructive/5"
                  : "bg-muted/40",
            )}
          >
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="flex min-w-0 items-start gap-3">
                <span className="mt-0.5 shrink-0">
                  {situacao.andando ? (
                    <Loader2 className="h-5 w-5 animate-spin text-primary" />
                  ) : lote.status === "failed" ? (
                    <AlertTriangle className="h-5 w-5 text-destructive" />
                  ) : lote.status === "completed" ? (
                    <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                  ) : (
                    <History className="h-5 w-5 text-muted-foreground" />
                  )}
                </span>

                <div className="min-w-0">
                  <p className="text-sm font-medium">
                    {situacao.rotulo}
                    {conexao?.name ? ` — ${conexao.name}` : ""}
                  </p>

                  <p className="mt-0.5 text-xs text-muted-foreground">
                    {mensagens} mensagem{mensagens === 1 ? "" : "s"} trazida
                    {mensagens === 1 ? "" : "s"}
                    {vivo.faltam
                      ? ` · ${vivo.faltam} de ${vivo.conversas} conversas ainda buscando`
                      : ""}
                    {recuouAte ? ` · já recuou até ${recuouAte}` : ""}
                  </p>

                  {/* O relatório aparece quando a rodada termina. Um total
                      sozinho não diz se deu certo: "0 novas" com tudo já
                      existindo é diferente de "0 novas" com mil falhas. */}
                  {!situacao.andando && (
                    <p className="mt-0.5 text-xs text-muted-foreground">
                      {lote.imported} nova(s) · {lote.existing} já existia(m) ·{" "}
                      {lote.skipped} fora da faixa · {lote.failed} falha(s)
                      {lote.oldestReceivedAt && lote.newestReceivedAt
                        ? ` · chegou de ${emData(lote.oldestReceivedAt)} a ${emData(
                            lote.newestReceivedAt,
                          )}`
                        : ""}
                    </p>
                  )}

                  {lote.sanitizedError && (
                    <p className="mt-0.5 text-xs text-destructive">
                      {lote.sanitizedError}
                    </p>
                  )}

                  {/* Dito toda vez que uma rodada termina, e não só quando dá
                      errado: o WhatsApp entrega o que ainda tem e não informa o
                      que deixou de entregar. Sem esta linha a tela diria
                      "concluída" e a pessoa pararia de procurar uma conversa
                      que simplesmente não veio. */}
                  {!situacao.andando && lote.status !== "failed" && (
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Esta é a faixa que o aparelho realmente entregou. O
                      WhatsApp não informa o que deixou de mandar.
                    </p>
                  )}
                </div>
              </div>

              <div className="flex shrink-0 gap-2">
                {situacao.andando ? (
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5"
                    disabled={parando === lote.id}
                    onClick={() => parar(lote)}
                  >
                    <Square className="h-3.5 w-3.5" />
                    Parar
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() =>
                      setDispensados((antes) => [...antes, lote.id])
                    }
                  >
                    Dispensar
                  </Button>
                )}
              </div>
            </div>

            {/* Barra sem porcentagem, de propósito.
                Não existe total: o WhatsApp não diz quantas mensagens vai
                mandar antes de mandar. Uma barra que fingisse 40% estaria
                inventando — esta só mostra que a coisa está viva. */}
            {situacao.andando && (
              <div className="mt-3 h-1 overflow-hidden rounded-full bg-primary/15">
                <div className="h-full w-1/3 animate-[indeterminado_1.4s_ease-in-out_infinite] rounded-full bg-primary" />
              </div>
            )}
          </div>
        );
      })}

      <style>{`
        @keyframes indeterminado {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
      `}</style>
    </div>
  );
};

export default ImportacaoEmAndamento;
